/**
 * auditLogger.js
 * src/utils/auditLogger.js
 *
 * SAKSHAM — Audit Log Utility
 * Every ADD / EDIT / DELETE / VIEW / LOGIN / LOGOUT is recorded here.
 *
 * Usage:
 *   import { logAudit } from '../utils/auditLogger';
 *   await logAudit({ user, action, module, targetId, targetName, changes, notes });
 *
 * IMPORTANT:
 *   - This function NEVER throws — main operation is never blocked.
 *   - Audit log is write-once. No update/delete allowed (Firestore rules).
 *   - Always uses Firestore serverTimestamp for consistency.
 *
 * Enhanced Device Info (v2):
 *   - Browser name + version
 *   - OS name + version
 *   - Device type (Mobile / Tablet / Desktop / Laptop)
 *   - Screen resolution
 *   - IP Address (via ipapi.co — free, 1000 req/day)
 *   - City + Region (Tamil Nadu)
 *   - ISP / Network provider
 */

// Firestore is lazy-imported inside _flushBatch() below, not here — this
// module is imported from App.jsx and Login.jsx (both render before any
// login happens), so a static import here would defeat the point of
// lazy-loading Firebase everywhere else.

// ─────────────────────────────────────────────────────────────────────────────
// ACTION CONSTANTS — use these everywhere, no typos
// ─────────────────────────────────────────────────────────────────────────────
export const AUDIT_ACTION = {
  LOGIN:          "LOGIN",
  LOGOUT:         "LOGOUT",
  LOGIN_FAILED:   "LOGIN_FAILED",
  ADD:            "ADD",
  EDIT:           "EDIT",
  DELETE:         "DELETE",
  VIEW:           "VIEW",
  STATUS_CHANGED: "STATUS_CHANGED",
  UPLOAD:         "UPLOAD",
  EXPORT:         "EXPORT",
  ROLE_CHANGED:   "ROLE_CHANGED",
  USER_ONLINE:    "USER_ONLINE",
  USER_OFFLINE:   "USER_OFFLINE",
  APP_HIT:        "APP_HIT",
};

// ─────────────────────────────────────────────────────────────────────────────
// MODULE CONSTANTS — matches AuditLog.jsx MODULE_ICONS keys
// ─────────────────────────────────────────────────────────────────────────────
export const AUDIT_MODULE = {
  AUTH:            "Auth",
  BENEFICIARY:     "Beneficiaries",
  SERVICE_REQUEST: "Service Request",
  MASTER_DATA:     "Master Data",
  PROJECTS:        "Projects",
  REPORTS:         "Reports",
  SETTINGS:        "Settings",
  USER:            "Settings",   // User management is inside Settings
  PRESENCE:        "Presence",
};

// ─────────────────────────────────────────────────────────────────────────────
// LEVEL LOGIC — auto-assigned based on action
// ─────────────────────────────────────────────────────────────────────────────
function resolveLevel(action) {
  if (action === AUDIT_ACTION.LOGIN_FAILED)                              return "warning";
  if ([AUDIT_ACTION.DELETE, AUDIT_ACTION.ROLE_CHANGED].includes(action)) return "warning";
  if ([AUDIT_ACTION.LOGIN, AUDIT_ACTION.LOGOUT, AUDIT_ACTION.VIEW,
       AUDIT_ACTION.EXPORT].includes(action))                            return "info";
  return "action"; // ADD, EDIT, STATUS_CHANGED, UPLOAD
}

// ─────────────────────────────────────────────────────────────────────────────
// BROWSER DETECTION — parses userAgent string
// ─────────────────────────────────────────────────────────────────────────────
function getBrowserInfo() {
  try {
    const ua = navigator.userAgent;

    // ── Browser name + version ──────────────────────────────────────────
    let browser = "Browser";
    let version = "";

    if (/Edg\/(\d+)/.test(ua)) {
      browser = "Edge";
      version = ua.match(/Edg\/(\d+)/)?.[1] || "";
    } else if (/OPR\/(\d+)/.test(ua) || /Opera\/(\d+)/.test(ua)) {
      browser = "Opera";
      version = ua.match(/OPR\/(\d+)/)?.[1] || ua.match(/Opera\/(\d+)/)?.[1] || "";
    } else if (/Firefox\/(\d+)/.test(ua)) {
      browser = "Firefox";
      version = ua.match(/Firefox\/(\d+)/)?.[1] || "";
    } else if (/Chrome\/(\d+)/.test(ua) && !/Chromium/.test(ua)) {
      browser = "Chrome";
      version = ua.match(/Chrome\/(\d+)/)?.[1] || "";
    } else if (/Version\/(\d+).*Safari/.test(ua)) {
      browser = "Safari";
      version = ua.match(/Version\/(\d+)/)?.[1] || "";
    } else if (/MSIE (\d+)/.test(ua) || /Trident.*rv:(\d+)/.test(ua)) {
      browser = "IE";
      version = ua.match(/MSIE (\d+)/)?.[1] || ua.match(/rv:(\d+)/)?.[1] || "";
    }

    return version ? `${browser} ${version}` : browser;
  } catch {
    return "Browser";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// OS DETECTION — parses userAgent string
// ─────────────────────────────────────────────────────────────────────────────
function getOSInfo() {
  try {
    const ua = navigator.userAgent;

    if (/Windows NT 10\.0/.test(ua)) return "Windows 10/11";
    if (/Windows NT 6\.3/.test(ua))  return "Windows 8.1";
    if (/Windows NT 6\.1/.test(ua))  return "Windows 7";
    if (/Windows/.test(ua))          return "Windows";
    if (/iPhone OS (\d+)/.test(ua)) {
      const v = ua.match(/iPhone OS (\d+)/)?.[1];
      return v ? `iOS ${v}` : "iOS";
    }
    if (/iPad.*OS (\d+)/.test(ua)) {
      const v = ua.match(/OS (\d+)/)?.[1];
      return v ? `iPadOS ${v}` : "iPadOS";
    }
    if (/Android (\d+)/.test(ua)) {
      const v = ua.match(/Android (\d+)/)?.[1];
      return v ? `Android ${v}` : "Android";
    }
    if (/Mac OS X (\d+[._]\d+)/.test(ua)) {
      const v = ua.match(/Mac OS X ([\d_.]+)/)?.[1]?.replace(/_/g, ".");
      return v ? `macOS ${v}` : "macOS";
    }
    if (/Linux/.test(ua)) return "Linux";

    return "Unknown OS";
  } catch {
    return "Unknown OS";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DEVICE TYPE DETECTION
// ─────────────────────────────────────────────────────────────────────────────
function getDeviceType() {
  try {
    const ua = navigator.userAgent;
    const w  = window.screen.width;

    // iPhone or Android phone (not tablet)
    if (/iPhone/.test(ua)) return "Mobile";
    if (/Android/.test(ua) && !/Tablet|Tab/.test(ua) && w < 768) return "Mobile";

    // iPad or Android tablet
    if (/iPad/.test(ua))   return "Tablet";
    if (/Android/.test(ua) && w >= 768) return "Tablet";

    // Desktop vs Laptop — heuristic: screen width
    // Laptops typically ≤ 1440px, desktops > 1440px
    if (w > 1440) return "Desktop";
    if (w >= 1024) return "Laptop";
    if (w >= 768)  return "Tablet";

    return "Mobile";
  } catch {
    return "Desktop";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN RESOLUTION
// ─────────────────────────────────────────────────────────────────────────────
function getScreenResolution() {
  try {
    return `${window.screen.width}×${window.screen.height}`;
  } catch {
    return "";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// IP + LOCATION — ipapi.co (Free, 1000 req/day, no API key needed)
// Returns: { ip, city, region, org }
// NEVER throws — returns empty object on failure
// ─────────────────────────────────────────────────────────────────────────────

// Module-level cache — one fetch per browser session
// Avoids repeated API calls for every audit log entry
let _ipCache = null;

async function getIPInfo() {
  // Return cached result if already fetched this session
  if (_ipCache !== null) return _ipCache;

  try {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 4000); // 4 second timeout

    const res = await fetch("https://ipapi.co/json/", {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();

    _ipCache = {
      ip:     data.ip     || "",
      city:   data.city   || "",
      region: data.region || "",
      org:    data.org    || "",   // ISP — e.g. "AS55836 Reliance Jio Infocomm Limited"
    };
  } catch (err) {
    // Network error, timeout, rate limit — silently fail
    console.warn("[AuditLog] IP fetch failed:", err?.message || err);
    _ipCache = { ip: "", city: "", region: "", org: "" };
  }

  return _ipCache;
}

// ─────────────────────────────────────────────────────────────────────────────
// ISP CLEANER — strips AS number prefix from org field
// "AS55836 Reliance Jio Infocomm Limited" → "Jio"
// "AS24560 Bharti Airtel Limited"         → "Airtel"
// "AS9829 National Internet Backbone"     → "BSNL"
// ─────────────────────────────────────────────────────────────────────────────
function cleanISP(org) {
  if (!org) return "";
  // Remove "ASxxxxx " prefix
  let clean = org.replace(/^AS\d+\s+/i, "").trim();

  // Common ISP name simplification
  if (/jio/i.test(clean))        return "Jio";
  if (/airtel/i.test(clean))     return "Airtel";
  if (/bsnl|national internet/i.test(clean)) return "BSNL";
  if (/vodafone|vi\b/i.test(clean)) return "Vi";
  if (/idea/i.test(clean))       return "Idea/Vi";
  if (/act\s/i.test(clean))      return "ACT";
  if (/hathway/i.test(clean))    return "Hathway";
  if (/tata/i.test(clean))       return "Tata";
  if (/excitel/i.test(clean))    return "Excitel";

  // Truncate if too long
  return clean.length > 30 ? clean.slice(0, 30) + "…" : clean;
}

// ─────────────────────────────────────────────────────────────────────────────
// BUILD FULL DEVICE INFO STRING — used in logEntry.deviceInfo (display)
// ─────────────────────────────────────────────────────────────────────────────
function buildDeviceString(browser, os, deviceType, resolution, ipInfo) {
  const parts = [
    `${browser} / ${os} / ${deviceType}`,
    resolution ? `${resolution}` : "",
  ].filter(Boolean).join(" / ");

  const location = [
    ipInfo.city,
    ipInfo.region !== ipInfo.city ? ipInfo.region : "",
  ].filter(Boolean).join(", ");

  const network = [
    ipInfo.ip   ? `IP: ${ipInfo.ip}` : "",
    location    ? location            : "",
    cleanISP(ipInfo.org),
  ].filter(Boolean).join(" | ");

  return network ? `${parts}\n${network}` : parts;
}

// ─────────────────────────────────────────────────────────────────────────────
// BATCH BUFFER — groups audit writes within a 2-second window into one
// Firestore writeBatch commit (max 400 entries to stay under the 500 limit).
// Fire-and-forget: logAudit never blocks the caller regardless.
// ─────────────────────────────────────────────────────────────────────────────
const BATCH_DELAY_MS = 2000;
const BATCH_MAX_SIZE = 400;

let _pendingEntries = [];
let _flushTimer     = null;

async function _flushBatch() {
  if (_pendingEntries.length === 0) return;
  const entries = _pendingEntries.splice(0); // drain the buffer
  _flushTimer = null;
  try {
    const [{ db }, { collection, writeBatch, doc, serverTimestamp }] = await Promise.all([
      import("../firebase"),
      import("firebase/firestore"),
    ]);
    const batch = writeBatch(db);
    entries.forEach(entry => {
      // timestamp is a server-side sentinel — fine to create it now rather
      // than when logAudit() was originally called, a few seconds earlier
      batch.set(doc(collection(db, "auditLogs")), { ...entry, timestamp: serverTimestamp() });
    });
    await batch.commit();
  } catch (err) {
    console.warn("[AuditLog] Batch flush failed:", err?.message || err);
  }
}

function _scheduleBatch() {
  if (_pendingEntries.length >= BATCH_MAX_SIZE) {
    // Flush immediately when the buffer is full
    if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null; }
    _flushBatch();
  } else {
    // Reset the debounce window
    if (_flushTimer) clearTimeout(_flushTimer);
    _flushTimer = setTimeout(_flushBatch, BATCH_DELAY_MS);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN LOGGER — call this everywhere
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {object} params
 * @param {object|null} params.user        — currentUser from AuthContext
 *                                           { uid, name, role, district, email }
 * @param {string}      params.action      — use AUDIT_ACTION constants
 * @param {string}      params.module      — use AUDIT_MODULE constants
 * @param {string}      [params.targetId]  — SAKSHAM ID / REQ ID / UID of affected record
 * @param {string}      [params.targetName]— Human-readable name of affected record
 * @param {object|null} [params.changes]   — { field, oldValue, newValue } for EDIT
 * @param {string|null} [params.notes]     — optional free-text reason
 */
export async function logAudit({
  user        = null,
  action,
  module,
  targetId    = null,
  targetName  = null,
  changes     = null,
  notes       = null,
}) {
  try {
    const level = resolveLevel(action);

    // ── Collect device info (all async-safe) ─────────────────────────────
    const browser    = getBrowserInfo();
    const os         = getOSInfo();
    const deviceType = getDeviceType();
    const resolution = getScreenResolution();
    const ipInfo     = await getIPInfo();   // cached after first call

    const deviceString = buildDeviceString(browser, os, deviceType, resolution, ipInfo);

    const logEntry = {
      // ── Timestamp is added in _flushBatch() — it needs the dynamically
      // imported serverTimestamp() sentinel, which isn't loaded yet here.

      // ── Who did it
      userId:       user?.uid        || "anonymous",
      userName:     user?.name       || "Unknown",
      userRole:     user?.role       || "unknown",
      userDistrict: user?.district   || "unknown",
      userEmail:    user?.email      || "unknown",

      // ── What happened
      action,
      module,
      level,

      // ── What was affected
      targetId:     targetId   || null,
      targetName:   targetName || null,

      // ── Change details (for EDIT)
      changes:      changes    || null,

      // ── Device + Network (structured — for AuditLog.jsx detail modal)
      deviceInfo:   deviceString,
      deviceDetail: {
        browser,
        os,
        deviceType,
        resolution,
        ip:     ipInfo.ip     || null,
        city:   ipInfo.city   || null,
        region: ipInfo.region || null,
        isp:    cleanISP(ipInfo.org) || null,
      },

      // ── Context
      notes:        notes      || null,
    };

    _pendingEntries.push(logEntry);
    _scheduleBatch();

  } catch (err) {
    // NEVER block the main operation — silent fail
    console.warn("[AuditLog] Failed to write log:", err?.message || err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER — build changes object cleanly for EDIT actions
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Compare old and new objects — returns array of changed fields only.
 * Use this in EDIT operations to log exactly what changed.
 *
 * @param {object} oldData
 * @param {object} newData
 * @param {string[]} [watchFields] — optional: only watch these fields
 * @returns {Array<{field, oldValue, newValue}>}
 *
 * Example:
 *   const changes = buildChanges(oldUser, newUser, ["role","status","name"]);
 *   await logAudit({ ..., changes });
 */
export function buildChanges(oldData, newData, watchFields = null) {
  try {
    const fields = watchFields || Object.keys({ ...oldData, ...newData });
    return fields
      .filter(f => JSON.stringify(oldData[f]) !== JSON.stringify(newData[f]))
      .map(f => ({
        field:    f,
        oldValue: oldData[f] ?? null,
        newValue: newData[f] ?? null,
      }));
  } catch {
    return [];
  }
}
